/**
 * useMentorApplicationStatus — **single source of truth** for the mentor-
 * application phase a user is in (header chip, profile-menu CTA,
 * dashboard role badge).
 *
 * State is module-scope (singleton): every component that uses the
 * composable sees and reacts to the SAME phase ref.  Writers
 * (BecomeMentorForm submit / cancel handlers, future WebSocket pushes)
 * call the exported `setMentorApplicationFromPayload()` /
 * `refreshMentorApplicationStatus()` directly — no event bus, no Vuex
 * action.  Consumers' chips re-render via Vue's reactivity.
 *
 * Bug (28.05.2026, second pass): первая версия создавала refs ВНУТРИ
 * composable'а — каждый header / dashboard / menu имел свою копию state.
 * После submit'а в BecomeMentorForm chip продолжал показывать «вы не
 * закончили заявку» до hard-reload.  Fix: lift refs to module scope +
 * expose write-side helpers.
 *
 * Why a composable, not a Vuex slice?  The audit
 * (`docs/audit/2026-05-28-role-status-chip-design.md` §2.3) explicitly
 * opts for minimal surface area: store-state addition triggers Vuex
 * boilerplate everywhere, and the data is read-only + single-request +
 * coupled to *that* route's lifecycle.  Containers (HeaderDefault,
 * DashboardPage, ProfileMenu) call this composable directly and pass
 * already-normalized props to the presentational chip.
 *
 * Phase enum mirrors the backend `MentorApplication.Status` 1:1 plus
 * an explicit `none` sentinel for the 404/no-application case (the
 * audit's "no_app" terminology), so consumers can switch on a single
 * union without a separate "exists?" boolean.
 *
 * Network errors degrade gracefully to `phase='none'` (audit §3.1 — the
 * chip stays visible, no error toast on header mount).
 */

import { ref, onMounted, type Ref } from 'vue';

import { getMyMentorApplicationApi } from '@/services/api';
import type { MentorApplicationStatus } from '@/shared/types';

/**
 * UI phase for the mentor-application chip + ProfileMenu CTA.
 *
 * Notes:
 *  - `none`     — there is no application (404 from /me/, or the user
 *                 cancelled and we treat that as "may apply").
 *  - `pending`  — backend `submitted`. The application is sitting in
 *                 the moderation queue.
 *  - `changes_requested` — backend told the user to amend the draft.
 *                 Visually behaves like `pending` (yellow tint, no CTA
 *                 hammer) but ProfileMenu surfaces a different label
 *                 ("Дополнить заявку").
 *  - `revoked`  — админ отозвал уже одобренный доступ (backend `revoked`,
 *                 `selling_readiness='application_rejected'`). Роль в
 *                 user-записи остаётся `mentor`, поэтому потребители
 *                 обязаны давать этой фазе приоритет над сырой ролью —
 *                 НЕ схлопывать в `none`/`approved`.
 */
export type Phase =
    | 'none'
    | 'draft'
    | 'pending'
    | 'approved'
    | 'rejected'
    | 'revoked'
    | 'changes_requested'
    | 'cancelled';

/**
 * Subset of the MentorApplication serializer that the chip+composable
 * read.  Keeping the shape narrow avoids importing the entire
 * `MentorApplicationPayload` type — we only need status + reason.
 */
export interface MentorApplicationLike {
    status?: MentorApplicationStatus | string | null;
    rejection_reason?: string | null;
}

/**
 * Pure mapper from backend MentorApplication → Phase.  Exported for
 * direct unit-tests and DRY reuse by MentorStatusCard (boy-scout work
 * scheduled in `docs/audit/2026-05-28-role-status-chip-design.md` §5.3).
 */
export function normalizePhase(app: MentorApplicationLike | null | undefined): Phase {
    if (!app || typeof app !== 'object') return 'none';
    const raw = app.status;
    if (typeof raw !== 'string') return 'none';
    switch (raw) {
        case 'draft':
            return 'draft';
        case 'submitted':
            return 'pending';
        case 'changes_requested':
            return 'changes_requested';
        case 'approved':
            return 'approved';
        case 'rejected':
            return 'rejected';
        case 'revoked':
            return 'revoked';
        case 'cancelled':
            return 'cancelled';
        default:
            return 'none';
    }
}

export interface MentorApplicationState {
    phase: Ref<Phase>;
    rejectionReason: Ref<string>;
    loading: Ref<boolean>;
    /**
     * Force a re-fetch from `/api/mentor-applications/me/`.  Useful after
     * the user submits the application elsewhere in the app or a
     * WebSocket pushes a status change (planned in Issue #6).
     */
    reload: () => Promise<void>;
}

// ─── Module-scope singleton state ─────────────────────────────────────
//
// All composable invocations share these refs.  Mutations from any code
// path (form submit, ws handler, manual reload) immediately reflect in
// every component that reads them via the composable.

const phase = ref<Phase>('none');
const rejectionReason = ref('');
const loading = ref(true);

let hasFetched = false;
let inFlight: Promise<void> | null = null;

async function fetchFromBackend(): Promise<void> {
    // Single-flight: parallel calls coalesce onto the same network round-trip.
    if (inFlight) return inFlight;
    loading.value = true;
    inFlight = (async () => {
        try {
            const data = (await getMyMentorApplicationApi()) as
                | MentorApplicationLike
                | null
                | undefined;
            setMentorApplicationFromPayload(data ?? null);
        } catch {
            // Network error: keep the chip visible with `none` phase
            // (audit §3.1) — do NOT surface a header-mount toast.
            phase.value = 'none';
            rejectionReason.value = '';
        } finally {
            loading.value = false;
        }
    })();
    try {
        await inFlight;
    } finally {
        inFlight = null;
    }
}

/**
 * Optimistic update: when the caller already has a fresh MentorApplication
 * payload in hand (e.g. immediately after `POST /submit/` or `/cancel/`
 * returned 200), feed it here to update the chip without a refetch.
 *
 * Pass `null` to mean "no application" — chip drops to `none` phase.
 */
export function setMentorApplicationFromPayload(
    app: MentorApplicationLike | null | undefined,
): void {
    phase.value = normalizePhase(app ?? null);
    const reason =
        app && typeof app === 'object' && typeof app.rejection_reason === 'string'
            ? app.rejection_reason
            : '';
    rejectionReason.value = reason;
    hasFetched = true;
}

/**
 * Module-level refresh — invalidates the cached "already fetched" flag
 * and forces a new network round-trip.  Call after writes when the
 * caller does NOT have the freshly-returned payload (or to double-check
 * the server's authoritative state).
 */
export async function refreshMentorApplicationStatus(): Promise<void> {
    await fetchFromBackend();
}

/**
 * Composable: returns the SHARED reactive state.  First call ensures
 * exactly one initial fetch is queued in `onMounted` — subsequent
 * components hop on the already-resolved refs.
 */
export function useMentorApplicationStatus(): MentorApplicationState {
    onMounted(() => {
        if (!hasFetched && !inFlight) {
            void fetchFromBackend();
        }
    });

    return {
        phase,
        rejectionReason,
        loading,
        reload: fetchFromBackend,
    };
}

/**
 * Test-only: reset module-scope singleton state so vitest cases are
 * isolated.  Not exported through the barrel — application code must
 * not depend on this.
 */
export function __resetMentorApplicationStatusForTests(): void {
    phase.value = 'none';
    rejectionReason.value = '';
    loading.value = true;
    hasFetched = false;
    inFlight = null;
}
